import { tryGetDirectory, readTextFiles, writeFile } from "../../storage/fsa.ts";
import { writeModuleIndex } from "../shared/module-index.ts";
import { renderVCardFile, renderCsvFile } from "./render.ts";
import { CONTACTS_RAW_DIR, CONTACTS_VCF_PATH, CONTACTS_CSV_PATH } from "./paths.ts";
import type { Contact } from "./types.ts";

/**
 * Rebuilds contacts.vcf and contacts.csv from the raw per-contact JSON files
 * already on disk — the same idempotent, network-free, read-from-disk-not-
 * memory pattern as Mail's `_index.json` and Teams' `index.html` render
 * passes. Safe to re-run after every resume, including on a contact list
 * that turns out to already be fully captured.
 *
 * Returns the same contacts so the caller can also rebuild contacts/index.html
 * without a second disk read.
 */
export async function renderContactFiles(root: FileSystemDirectoryHandle): Promise<Contact[]> {
    const dir = await tryGetDirectory(root, CONTACTS_RAW_DIR.split("/"));
    if (!dir) {
        return [];
    }

    const contacts: Contact[] = [];
    for await (const { name, text } of readTextFiles(dir)) {
        if (!name.endsWith(".json")) {
            continue;
        }
        try {
            contacts.push(JSON.parse(text) as Contact);
        } catch {
            // A corrupt raw file is skipped rather than failing the whole
            // render — it stays on disk, visible via export.log or manual
            // review, just absent from this pass's output files.
        }
    }

    await writeFile(root, CONTACTS_VCF_PATH, renderVCardFile(contacts));
    await writeFile(root, CONTACTS_CSV_PATH, renderCsvFile(contacts));
    return contacts;
}

/**
 * Rebuilds `contacts/index.html` from the contacts renderContactFiles already
 * read. There is no per-contact file to link to (only the two aggregate
 * outputs above), so rows carry no href — the vCard/CSV links above the
 * table are the "open this" affordance instead.
 */
export async function renderContactsIndex(
    root: FileSystemDirectoryHandle,
    contacts: readonly Contact[]
): Promise<void> {
    const sorted = [...contacts].sort((a, b) =>
        (a.displayName ?? "").localeCompare(b.displayName ?? "")
    );

    await writeModuleIndex(root, {
        moduleId: "contacts",
        itemKind: "data",
        title: "Contacts",
        subtitle: `${sorted.length} contact${sorted.length === 1 ? "" : "s"} exported`,
        columns: [
            { key: "name", label: "Name" },
            { key: "email", label: "Email" },
            { key: "phone", label: "Phone" },
            { key: "company", label: "Company" }
        ],
        rows: sorted.map((c) => ({
            cells: {
                name: c.displayName || "(no name)",
                email: c.emailAddresses?.[0]?.address ?? "",
                phone: c.mobilePhone || c.businessPhones?.[0] || c.homePhones?.[0] || "",
                company: c.companyName ?? ""
            }
        })),
        emptyMessage: "No contacts exported yet.",
        searchPlaceholder: "Filter contacts…",
        relatedLinks: [
            { label: "contacts.vcf", href: "contacts.vcf" },
            { label: "contacts.csv", href: "contacts.csv" }
        ]
    });
}
